// Meta Ads API integration and spend ingestion
// Note: In production, use a server-side proxy for tokens.

import { supabase } from './supabase';
import { noderr, DatabaseError } from '../../shared/utils/errorHandler';

type MetaSpendHour = {
  external_campaign_id: string;
  hour_utc: string; // ISO hour
  amount: number; // in account currency
  currency: string;
  raw?: any;
};

export class MetaAdsService {
  // Mapping helpers
  async getBudgetMetaMappings(budgetIds: string[]) {
    if (!budgetIds.length) return [] as { id: string; external_campaign_id: string; budget_id: string | null }[];
    const { data, error } = await supabase
      .from('campaign_external_mappings')
      .select('id, external_campaign_id, budget_id')
      .eq('platform', 'meta')
      .in('budget_id', budgetIds);
    if (error) throw new DatabaseError(`Failed to load Meta mappings: ${error.message}`);
    return (data as any) || [];
  }

  async saveBudgetMetaMapping(options: { budgetId: string; externalCampaignId: string; campaignId?: string; notes?: string }) {
    const { budgetId, externalCampaignId, campaignId, notes } = options;
    // Remove any previous mapping for this budget
    const del = await supabase
      .from('campaign_external_mappings')
      .delete()
      .eq('platform', 'meta')
      .eq('budget_id', budgetId);
    if (del.error) throw new DatabaseError(`Failed to clear existing mapping: ${del.error.message}`);

    const insertPayload = {
      platform: 'meta',
      external_campaign_id: externalCampaignId,
      budget_id: budgetId,
      campaign_id: campaignId || null,
      notes: notes || null,
    } as const;
    const { data, error } = await supabase
      .from('campaign_external_mappings')
      .upsert(insertPayload, { onConflict: 'platform,external_campaign_id' })
      .select('id')
      .single();
    if (error) throw new DatabaseError(`Failed to save mapping: ${error.message}`);
    return data;
  }

  async unlinkBudgetMetaMapping(budgetId: string) {
    const { error } = await supabase
      .from('campaign_external_mappings')
      .delete()
      .eq('platform', 'meta')
      .eq('budget_id', budgetId);
    if (error) throw new DatabaseError(`Failed to unlink mapping: ${error.message}`);
    return { ok: true };
  }

  // Fetch hourly spend via a proxy endpoint; avoid exposing tokens client-side
  async fetchHourlySpend(sinceISO: string, untilISO: string, campaignIds?: string[]): Promise<MetaSpendHour[]> {
    const proxy = (import.meta as any).env?.VITE_META_SPEND_PROXY_URL || '/api/meta/spend';
    const base = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
    const url = proxy.startsWith('http') ? new URL(proxy) : new URL(proxy, base);
    url.searchParams.set('since', sinceISO);
    url.searchParams.set('until', untilISO);
    if (campaignIds && campaignIds.length) url.searchParams.set('campaign_ids', campaignIds.join(','));

    // Forward Supabase session for RLS-protected integration_settings
    let headers: Record<string, string> | undefined;
    try {
      const { supabase } = await import('./supabase');
      const session = await (supabase as any)?.auth?.getSession?.();
      const token = session?.data?.session?.access_token;
      if (token) headers = { Authorization: `Bearer ${token}` };
    } catch {}

    const resp = await fetch(url.toString(), { headers });
    if (!resp.ok) throw new Error(`Meta proxy error: ${resp.status}`);
    const data = await resp.json();
    // Expect normalized array from proxy; otherwise map here.
    return (data as MetaSpendHour[]) || [];
  }

  // Upsert spend events into ad_spend_events with unique (platform, external_campaign_id, event_hour)
  async ingestHourlySpend(events: MetaSpendHour[], campaignId?: string, budgetId?: string) {
    if (!events.length) return { inserted: 0 };

    const rows = events.map(e => ({
      platform: 'meta',
      external_campaign_id: e.external_campaign_id,
      campaign_id: campaignId || null,
      budget_id: budgetId || null,
      amount: e.amount,
      currency: e.currency || 'USD',
      event_hour: e.hour_utc,
      source: 'api_import',
      raw: e.raw || null,
    }));

    const [error, result] = await noderr(
      supabase.from('ad_spend_events')
        .upsert(rows, { onConflict: 'platform,external_campaign_id,event_hour' })
        .select('id')
    );
    if (error) throw new DatabaseError(`Failed to upsert Meta spend events: ${error.message}`);
    const data = (result as any)?.data as any[] | undefined;
    return { inserted: data?.length || 0 };
  }

  // Reconcile spent_amount on campaign_budgets from ad_spend_events
  async reconcileBudgetSpent(budgetId: string) {
    const [sumErr, sumRes] = await noderr(
      supabase.from('ad_spend_events')
        .select('amount')
        .eq('budget_id', budgetId)
    );
    if (sumErr) throw new DatabaseError(`Failed to get spend totals: ${sumErr.message}`);
    const rows = (sumRes as any)?.data as any[] | undefined;
    const total = (rows || []).reduce((s: number, r: any) => s + (r.amount || 0), 0);

    const [updErr] = await noderr(
      supabase.from('campaign_budgets')
        .update({ spent_amount: total })
        .eq('id', budgetId)
    );
    if (updErr) throw new DatabaseError(`Failed to update budget spent amount: ${updErr.message}`);
    return { total };
  }
}

export const metaAdsService = new MetaAdsService();
